import type { Configurator } from "@models/configurators/configurator";
import { Enumerable, Kbo } from "@models/configurators/configurator-di";
import { CToken } from "@models/configurators/ctoken";
import { defaults } from "@models/configurators/default-values";
import { KbObjectManager } from "@models/configurators/kb-object-manager";
import { IKbObject } from "@models/entity-models";
import { KbEvent, Utils } from "@tools";



export interface IPropertyChangedArgs {
    property: string;
    oldValue: any;
    newValue: any;
}
export class PropertyChangedEvent extends KbEvent<IPropertyChangedArgs> { }

export interface IDummyNode {
    id: string;
    $expanded?: boolean;
    $type: string;
    allowedChildren(rootType?: CToken): CToken[];
    getParent: () => KbObject;
}

@Kbo(CToken.KbObject)
export class KbObject implements IKbObject, IDummyNode {
    constructor(public $manager: KbObjectManager) {
        Utils.guardNotNull($manager);

        // set defaults
        let defaultsClass = defaults[this.$type];
        for (let prop in defaultsClass) {
            this[prop] = defaultsClass[prop];
        }

        this.id = Utils.shortId();
    }

    public $type: string;
    public $expanded: boolean;
    public $propertyChanged = new PropertyChangedEvent();
    public label: string;
    public description: string;

    public static generateName(obj: KbObject, parentGroup: any[], name?: string) {
        if (!name) name = obj.$type;
        let i = 0;
        let resultName = name;
        parentGroup.forEach(kb => {
            if (kb.name == resultName) {
                resultName = name + (++i);
            }
        });
        obj.name = resultName;
    }

    /**
     * a helper that derived classes can call from property setters to enable change detection.  
     * NOTE!  Assumes there is a private backing field
     * for the property that is named by the convention "_" + propertyName
     */
    protected setProperty(propertyName: string, newValue, addToUndoStack: boolean = false) {
        let privateProp = "_" + propertyName;
        let oldValue = this[privateProp];
        let changed = (oldValue !== newValue);
        // set the private backing variable
        this[privateProp] = newValue;
        if (changed) {
            this.$propertyChanged.trigger({
                oldValue,
                newValue,
                property: propertyName
            });
        }
    }

    private _id: string;
    @Enumerable()
    public get id(): string {
        return this._id;
    }
    public set id(val: string) {
        let changed = !Utils.areEqual(this._id, val);

        if (changed) {
            // delete from name scope
            this.$manager.removeKbObjectById(this._id);
            this.$manager.addKbObjectWithId(val, this);
        }
        this._id = val;
    }

    public $parent: KbObject;

    public getParentOfType($type: string): KbObject {
        let parent: KbObject = this.$parent;
        while (parent) {
            if (parent.$type == $type) return parent;
            parent = parent.$parent;
        }
        return null;
    }

    public findAncestor(predicate: (kbo) => boolean) {
        let parent: KbObject = this.$parent;
        while (parent) {
            if (predicate(parent)) return parent;
            parent = parent.$parent;
        }
        return null;
    }
    
    private _parentConfigurator: Configurator;
    @Enumerable()
    public get $parentConfigurator(): Configurator {
        if (!this._parentConfigurator) {
            this._parentConfigurator = this.getParentOfType("Configurator") as Configurator;
        }
        return this._parentConfigurator;
    }

    private _name: string;
    @Enumerable()
    public get name(): string {
        return this._name;
    }
    public set name(val: string) {
        let changed = !Utils.areEqual(this._name, val);

        if (changed) {
            val = this.$manager.getUniqueName(this, val);
            // delete from name scope
            if (this._name) {
                this.$manager.removeKbObjectNameReference(this._name);
            }
            this.$manager.addKbObjectNameReference(val, this);
        }
        this._name = val;
    }

    /**
     * for field controls to bind to.  Defaults to name if there is no label
     */
    @Enumerable()
    public get $label(): string {
        let l = this.label ? this.label : this.name;
        return l;
    }

    public serialize(modifier?: (kbo: KbObject, serialized: {}) => void): {} {
        // Not all objects have complex getters and setters, so we'll default to this.
        let result: any = {};

        result.$type = this.$type; // type needs to be first for the deserialization to happen correctly on the server
        for (let key in this) {
            if (key.substr(0, 1) == "$" || (key.substr(0, 1) == "_" && key != "_metadata") || this.ignoredProperties().some(p => p.isEqual(key))) continue;
            let item: any = this[key] as any;
            if (item == this) continue;
            if (item instanceof Function) continue;
            if (item && item.constructor === Array && item[0] && item[0].serialize) {
                result[key] = item.map((i: KbObject) => i.serialize(modifier));
            } else if (item && item.serialize) {
                result[key] = (item as KbObject).serialize(modifier);
            } else {
                result[key] = item;
            }
        }
        result.name = this.name;
        result.label = this.label;
        result.id = this.id;
        if (modifier) modifier(this, result);
        return result;
    }

    /**
     * sets property values of the class given an object.  For example:  {name: "field1", value: 15}
     * Used by snap so don't delete!
     * @param values
     */
    public setProps(values: {}) {
        for (let key in values) {
            this[key] = values[key];
        }
    }

    /**
     * Can be overridden by derived classes to specify properties that should be ignored during
     * serialization
     */
    public ignoredProperties(): string[]{
        return [];
    }

    public allowedChildren(rootType: CToken): CToken[] {
        return [];
    }

    public getParent = () => this.$parent;
}
